import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CreditCard, LogOut, Clock, ShieldAlert } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../lib/api';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock.js';

export function SubscriptionBlocker({ subscriptionStatus: initialStatus }) {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  
  // Bloquear scroll del body cuando el modal está visible
  useBodyScrollLock(isVisible);

  const { data: subscriptionData, isLoading } = useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: async () => {
      const { data } = await api.get('/api/subscription/status/');
      return data;
    },
    enabled: true,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });

  useEffect(() => {
    if (subscriptionData && !isLoading) {
      const status = subscriptionData.subscription_status;
      if (status === 'SUSPENDED' || status === 'CANCELLED' || status === 'PENDING_PAYMENT') {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } else if (initialStatus?.isSuspended) {
      setIsVisible(true);
    }
  }, [subscriptionData, isLoading, initialStatus]);

  const handleLogout = () => {
    // Limpiar ambos tokens
    localStorage.removeItem('tiendaya_token');
    localStorage.removeItem('tiendaya_refresh_token');
    navigate('/inicio-sesion');
  };

  if (!isVisible) return null;

  // CONFIGURACIÓN VISUAL SEGÚN ESTADO
  const getBlockerContent = () => {
    if (isLoading || !subscriptionData) {
      return {
        type: 'loading', title: 'Verificando...', message: 'Consultando estado de cuenta.',
        icon: Clock, color: 'text-white/50', bg: 'bg-white/5', border: 'border-white/10',
        btn: false
      };
    }
    
    const status = subscriptionData.subscription_status;

    if (status === 'PENDING_PAYMENT') {
      return {
        type: 'pending',
        title: 'Verificando Pago',
        message: 'Tu pago está en revisión. El acceso se restaurará automáticamente al aprobarse.',
        icon: Clock,
        color: 'text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        shadow: 'shadow-[0_0_30px_rgba(245,158,11,0.15)]',
        btn: false,
        btnText: 'Entendido'
      };
    }

    if (status === 'SUSPENDED') {
      // Detectar si viene de trial (nunca tuvo plan pago) o es renovación
      const isFromTrial = subscriptionData.is_first_subscription || false;
      
      if (isFromTrial) {
        // Trial finalizado - Primera suscripción
        return {
          type: 'trial_ended',
          title: 'Prueba Gratis Finalizada',
          message: 'Tu período de prueba ha terminado. Suscríbete a un plan para continuar vendiendo.',
          icon: AlertTriangle,
          color: 'text-red-500',
          bg: 'bg-red-500/10',
          border: 'border-red-500/30',
          shadow: 'shadow-[0_0_30px_rgba(239,68,68,0.2)]',
          btn: true,
          btnText: 'Ver Planes'
        };
      } else {
        // Suscripción paga vencida - Renovación
        return {
          type: 'expired',
          title: 'Suscripción Vencida',
          message: 'Tu plan ha expirado. Renueva ahora para reactivar tu tienda y seguir vendiendo.',
          icon: AlertTriangle,
          color: 'text-red-500',
          bg: 'bg-red-500/10',
          border: 'border-red-500/30',
          shadow: 'shadow-[0_0_30px_rgba(239,68,68,0.2)]',
          btn: true,
          btnText: 'Renovar Plan'
        };
      }
    }

    return {
      type: 'suspended', title: 'Cuenta Inactiva', message: 'Acceso suspendido. Contacta a soporte.',
      icon: ShieldAlert, color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/30', shadow: '',
      btn: false, btnText: 'Cerrar Sesión'
    };
  };

  const content = getBlockerContent();
  const IconComponent = content.icon;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-xl animate-in fade-in duration-500">
      
      <div className={`relative w-full max-w-md rounded-[2.5rem] border ${content.border} bg-[#0A0A0A] p-8 md:p-10 shadow-2xl overflow-hidden ${content.shadow}`}>
        
        {/* Luz ambiental */}
        <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-current to-transparent opacity-50 ${content.color}`} />

        {/* Icono */}
        <div className="flex justify-center mb-6">
           <div className={`p-4 rounded-2xl ${content.bg} border ${content.border} animate-pulse`}>
              <IconComponent className={`h-10 w-10 ${content.color}`} />
           </div>
        </div>

        {/* Textos */}
        <div className="text-center mb-8">
           <h2 className="text-2xl font-bold text-white tracking-tight mb-2">{content.title}</h2>
           <p className="text-white/60 text-sm leading-relaxed">{content.message}</p>
        </div>

        {/* Caja de Info Extra (Solo si no es loading) */}
        {content.type !== 'loading' && (
           <div className={`mb-8 p-4 rounded-xl border ${content.border} ${content.bg} flex items-start gap-3`}>
              <ShieldAlert className={`h-5 w-5 flex-shrink-0 ${content.color}`} />
              <div className="text-left">
                 <p className={`text-xs font-bold uppercase tracking-widest ${content.color} mb-1`}>
                   {content.type === 'pending' ? 'Pago en Revisión' : 'Acceso Suspendido'}
                 </p>
                 <p className="text-xs text-white/70">
                    {content.type === 'pending' 
                      ? 'Tu pago será verificado en 24-48h. Recibirás notificación cuando se apruebe.' 
                      : content.type === 'trial_ended'
                      ? 'Elige un plan para continuar usando tu tienda.'
                      : 'Renueva tu plan para recuperar acceso completo a tu tienda.'}
                 </p>
              </div>
           </div>
        )}

        {/* Acciones */}
        <div className="space-y-3">
           {content.btn ? (
              <Link to="/precios" className={`group w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-gray-200 transition-all transform hover:scale-[1.02]`}>
                 <CreditCard className="h-4 w-4" /> {content.btnText}
              </Link>
           ) : (
              <button onClick={handleLogout} disabled={content.type === 'loading'} className="w-full py-3 rounded-xl bg-white/10 text-white text-xs font-bold uppercase tracking-widest hover:bg-white/20 transition-all border border-white/10">
                 {content.btnText}
              </button>
           )}
           
           {content.btn && (
              <button onClick={handleLogout} className="w-full py-3 rounded-xl border border-white/10 text-white/40 text-xs font-bold uppercase tracking-widest hover:text-white hover:border-white/30 transition-all">
                 <LogOut className="h-3 w-3 inline mr-2" /> Cerrar Sesión
              </button>
           )}
        </div>

      </div>
    </div>
  );
}